import React from "react";
import { useZipCodeLookup } from "../hooks/useZipCodeLookup";

/* ===============================
   Franklin, NJ Date Utilities
   =============================== */

const FRANKLIN_TIMEZONE = "America/New_York";

// ✅ Get today's date in Franklin (YYYY-MM-DD)
const getFranklinDate = () => {
  return new Date().toLocaleDateString("en-CA", {
    timeZone: FRANKLIN_TIMEZONE,
  });
};

// ✅ Parse date safely (no timezone jump)
const parseFranklinDate = (dateString) => {
  if (!dateString) return null;
  return new Date(`${dateString}T00:00:00`);
};

// ✅ Minimum selectable date = tomorrow (Franklin)
const getMinDate = () => {
  const today = parseFranklinDate(getFranklinDate());
  today.setDate(today.getDate() + 1);
  return today.toISOString().split("T")[0];
};

/* ===============================
   Component
   =============================== */

const CustomerInfoForm = ({ customerInfo, setCustomerInfo }) => {
  const { handleZipChange } = useZipCodeLookup({
    customerInfo,
    setCustomerInfo,
  });

  const inputClasses =
    "w-full px-4 py-3 bg-white border border-indigo-100 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 placeholder-gray-400 text-gray-700 shadow-sm";

  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="slide-in">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
          Laminance Order
        </h1>
        <p className="text-gray-600 text-lg">
          Create beautiful cabinet orders with ease
        </p>
      </div>

      {/* Card */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-8 border border-indigo-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <div className="w-2 h-6 bg-indigo-500 rounded-full mr-3"></div>
          Customer Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label className={labelClasses}>First Name</label>
            <input
              type="text"
              className={inputClasses}
              placeholder="Enter first name"
              value={customerInfo.firstName}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  firstName: e.target.value,
                })
              }
            />
          </div>

          {/* Last Name */}
          <div>
            <label className={labelClasses}>Last Name</label>
            <input
              type="text"
              className={inputClasses}
              placeholder="Enter last name"
              value={customerInfo.lastName}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  lastName: e.target.value,
                })
              }
            />
          </div>

          {/* Email */}
          <div>
            <label className={labelClasses}>Email Address</label>
            <input
              type="email"
              className={inputClasses}
              placeholder="Enter email address"
              value={customerInfo.email}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  email: e.target.value,
                })
              }
            />
          </div>

          {/* Phone */}
          <div>
            <label className={labelClasses}>Phone Number</label>
            <input
              type="text"
              className={inputClasses}
              placeholder="Enter phone number"
              value={customerInfo.phone}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  phone: e.target.value,
                })
              }
            />
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label className={labelClasses}>Address</label>
            <textarea
              className={`${inputClasses} resize-none`}
              placeholder="Enter complete address"
              rows="3"
              value={customerInfo.address}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  address: e.target.value,
                })
              }
            />
          </div>

          {/* Zip */}
          <div>
            <label className={labelClasses}>Zip Code</label>
            <input
              type="text"
              className={inputClasses}
              placeholder="Enter zip code"
              value={customerInfo.zip}
              onChange={handleZipChange}
            />
          </div>

          {/* City */}
          <div>
            <label className={labelClasses}>City</label>
            <input
              type="text"
              className={`${inputClasses} bg-indigo-50`}
              placeholder="City"
              value={customerInfo.city}
              readOnly
            />
          </div>

          {/* State */}
          <div>
            <label className={labelClasses}>State</label>
            <input
              type="text"
              className={`${inputClasses} bg-indigo-50`}
              placeholder="State"
              value={customerInfo.state}
              readOnly
            />
          </div>

          {/* ✅ Order Date (Franklin-safe) */}
          <div>
            <label className={labelClasses}>Order Date</label>
            <input
              type="date"
              className={inputClasses}
              value={customerInfo.date}
              min={getMinDate()}
              onChange={(e) => {
                const selectedDate = e.target.value;

                const today = parseFranklinDate(getFranklinDate());
                const selected = parseFranklinDate(selectedDate);

                // ❌ Block today & past dates
                if (selected < today) {
                  alert(
                    "Please select a future date (Franklin, NJ time)"
                  );
                  return;
                }

                setCustomerInfo({
                  ...customerInfo,
                  date: selectedDate,
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoForm;
